import React, { useCallback, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { PROMOTION_REPLY_LIST_PAGE_ITEM_COUNT } from '@diritto/common/promotion/2025-balance-ritto.constants';
import { PromotionReplyOrder } from '@diritto/common/promotion/constants';
import { PromotionReplyDto } from '@diritto/common/promotion/dtos/promotion-reply.dto';
import { useGetPromotionRepliesQuery } from 'queries/promotions/useGetPromotionRepliesQuery';
import { EmptyList } from 'ui-components/common/EmptyList';
import { PromotionReply } from 'app-components/promotions/PromotionReply';
import { useGetPromotionLikedRepliesQueries } from 'queries/promotions/useGetPromotionLikedRepliesQuery';
import { useAuthState } from 'contexts/AuthProvider';
import { PromotionReplyListPaginator } from 'app-components/promotions/PromotionReplyListPaginator';
import { Loading } from 'ui-components/common/Loading';

interface PromotionReplyListProps {
  className?: string;
  promotionId: number;
  orderBy: PromotionReplyOrder;
  promotionItemId?: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onWeeklyCountChange: (count: number) => void;
}

export const PromotionReplyList = ({
  className,
  promotionId,
  orderBy,
  promotionItemId,
  currentPage,
  onPageChange,
  onWeeklyCountChange,
}: PromotionReplyListProps) => {
  const { isLoggedIn } = useAuthState();
  const { isLoading, data, refetch } = useGetPromotionRepliesQuery(
    {
      offset: PROMOTION_REPLY_LIST_PAGE_ITEM_COUNT * (currentPage - 1),
      limit: PROMOTION_REPLY_LIST_PAGE_ITEM_COUNT,
      promotionId,
      promotionItemId: promotionItemId ?? 0,
      orderBy,
    },
    !!promotionItemId,
  );

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      onWeeklyCountChange(data.totalCount);
    }
  }, [data?.totalCount, onWeeklyCountChange]);

  const { replyIds, replies, profiles } = useMemo(
    () => ({
      replyIds: data?.replyIds ?? [],
      replies: data?.replies ?? {},
      profiles: data?.profiles ?? {},
    }),
    [data],
  );

  const queries = useGetPromotionLikedRepliesQueries(promotionId, replyIds, replyIds.length > 0 && isLoggedIn);
  const likeReplyIds: string[] = useMemo(
    () => queries.reduce((acc, query) => [...acc, ...(query.data?.likeReplyIds ?? [])], [] as string[]),
    [queries],
  );

  const renderReply = useCallback(
    (reply: PromotionReplyDto) => {
      if (!reply.profileId) {
        return null;
      }

      return (
        <li key={reply.id}>
          {reply.deletedAt ? (
            <div> 비공개된 댓글이에요. </div>
          ) : (
            <PromotionReply
              promotionId={promotionId}
              reply={reply}
              profile={profiles[reply.profileId]}
              isLiked={likeReplyIds.includes(reply.id)}
              refetch={refetch}
            />
          )}
        </li>
      );
    },
    [profiles, likeReplyIds, promotionId, refetch],
  );

  const renderEmptyList = useCallback(() => <EmptyList title="첫 댓글을 남겨보세요." />, []);

  const renderLoading = useCallback(
    () => (
      <div className="flex h-[50vh] justify-center items-center">
        <Loading />
      </div>
    ),
    [],
  );

  return (
    <>
      <StyledContainer className={className}>
        {isLoading && renderLoading()}
        {!isLoading && replyIds.length === 0
          ? renderEmptyList()
          : replyIds.map(replyId => renderReply(replies[replyId]))}
      </StyledContainer>
      {replyIds.length > 0 && (
        <PromotionReplyListPaginator
          className="self-stretch inline-flex justify-between items-center"
          currentPage={currentPage}
          totalPage={Math.ceil((data?.totalCount ?? 0) / PROMOTION_REPLY_LIST_PAGE_ITEM_COUNT)}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};

const StyledContainer = styled.ul`
  min-height: 50vh;
`;
